<template>
	<div class="price-chart">
		<div class="price-chart__head">
			<span class="price-chart__symbol">{{ symbolName }}</span>
			<span class="price-chart__price" :class="[upOrDownClass, flashDir]">{{ fmtPrice(latestPrice) }}</span>
			<span class="price-chart__delta" :class="upOrDownClass">{{ deltaText }}</span>
		</div>
		<canvas ref="canvas" class="price-chart__canvas"></canvas>
	</div>
</template>

<script>
import { fmtPrice } from '@/util/fx/format'

/**
 * Canvas 坐标轴字体
 */
const AXIS_FONT = '11px "Cascadia Mono", Consolas, monospace'

/**
 * Canvas 价格标签牌字体
 */
const LABEL_FONT = 'bold 11px "Cascadia Mono", Consolas, monospace'

/**
 * 上涨色（与设计令牌保持一致）
 */
const UP_COLOR = '#f5564a'

/**
 * 下跌色（与设计令牌保持一致）
 */
const DOWN_COLOR = '#2ebd85'

/**
 * 绘制圆角矩形路径
 *
 * @param {object} ctx 画布上下文
 * @param {number} x 左上角 x 坐标
 * @param {number} y 左上角 y 坐标
 * @param {number} w 宽度
 * @param {number} h 高度
 * @param {number} r 圆角半径
 */
function roundRectPath(ctx, x, y, w, h, r) {
	ctx.beginPath()
	ctx.moveTo(x + r, y)
	ctx.arcTo(x + w, y, x + w, y + h, r)
	ctx.arcTo(x + w, y + h, x, y + h, r)
	ctx.arcTo(x, y + h, x, y, r)
	ctx.arcTo(x, y, x + w, y, r)
	ctx.closePath()
}

export default {
	name: 'PriceChart',
	props: {
		klines: {
			type: Array,
			default: () => []
		},
		latestPrice: {
			type: Number,
			default: 0
		},
		openPriceRef: {
			type: Number,
			default: null
		},
		symbolName: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			upClass: 'is-up',
			downClass: 'is-down',
			flashDir: ''
		}
	},
	computed: {
		/**
		 * 涨跌样式类
		 */
		upOrDownClass() {
			const last = this.klines[this.klines.length - 1]
			if (!last || !this.latestPrice) {
				return ''
			}
			return this.latestPrice >= last.close ? this.upClass : this.downClass
		},
		/**
		 * 当日涨跌幅文本
		 */
		deltaText() {
			const last = this.klines[this.klines.length - 1]
			if (!last || !last.open) {
				return ''
			}
			const delta = ((this.latestPrice - last.open) / last.open) * 100
			const sign = delta > 0 ? '+' : ''
			return sign + delta.toFixed(2) + '%'
		}
	},
	watch: {
		klines() {
			this.draw()
		},
		latestPrice(value, oldValue) {
			this.triggerFlash(value, oldValue)
			this.draw()
		}
	},
	mounted() {
		this.resizeObserver = new ResizeObserver(() => this.draw())
		this.resizeObserver.observe(this.$refs.canvas)
		this.draw()
	},
	beforeDestroy() {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect()
		}
		clearTimeout(this._flashTimer)
	},
	methods: {
		fmtPrice,
		/**
		 * 价格变动时触发涨跌闪动动画
		 *
		 * @param {number} value 新价格
		 * @param {number} oldValue 旧价格
		 */
		triggerFlash(value, oldValue) {
			if (!value || !oldValue || value === oldValue) {
				return
			}
			this.flashDir = value > oldValue ? 'is-flash-up' : 'is-flash-down'
			clearTimeout(this._flashTimer)
			this._flashTimer = setTimeout(() => {
				this.flashDir = ''
			}, 600)
		},
		/**
		 * 绘制 K 线图
		 */
		draw() {
			const canvas = this.$refs.canvas
			if (!canvas) {
				return
			}
			const rect = canvas.getBoundingClientRect()
			if (rect.width === 0) {
				return
			}
			const dpr = window.devicePixelRatio || 1
			canvas.width = rect.width * dpr
			canvas.height = rect.height * dpr
			const ctx = canvas.getContext('2d')
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

			const width = rect.width
			const height = rect.height
			const padTop = 14
			const padBottom = 18
			const padRight = 70
			const padLeft = 10
			const plotW = width - padLeft - padRight
			const plotH = height - padTop - padBottom

			ctx.clearRect(0, 0, width, height)

			const klines = this.klines
			if (klines.length === 0) {
				this.drawEmpty(ctx, width, height)
				return
			}

			let min = Infinity
			let max = -Infinity
			for (const k of klines) {
				if (k.low < min) min = k.low
				if (k.high > max) max = k.high
			}
			if (this.latestPrice > max) max = this.latestPrice
			if (this.latestPrice < min) min = this.latestPrice
			if (this.openPriceRef != null) {
				if (this.openPriceRef > max) max = this.openPriceRef
				if (this.openPriceRef < min) min = this.openPriceRef
			}
			const range = max - min || 1
			min -= range * 0.08
			max += range * 0.08
			const totalRange = max - min

			const yOf = price => padTop + ((max - price) / totalRange) * plotH
			const barWidth = plotW / klines.length
			const candleWidth = Math.max(1, barWidth * 0.7)

			// 横向网格与价格刻度
			ctx.font = AXIS_FONT
			ctx.textAlign = 'left'
			ctx.textBaseline = 'middle'
			for (let i = 0; i <= 4; i++) {
				const y = padTop + (plotH * i) / 4
				const price = max - (totalRange * i) / 4
				ctx.strokeStyle = 'rgba(148, 170, 220, 0.07)'
				ctx.lineWidth = 1
				ctx.beginPath()
				ctx.moveTo(padLeft, y)
				ctx.lineTo(padLeft + plotW, y)
				ctx.stroke()
				ctx.fillStyle = 'rgba(205, 216, 236, 0.6)'
				ctx.fillText(fmtPrice(price), padLeft + plotW + 8, y)
			}

			// 纵向时间网格（每 12 根 K 线一条淡线）
			ctx.strokeStyle = 'rgba(148, 170, 220, 0.045)'
			for (let i = 12; i < klines.length; i += 12) {
				const x = padLeft + barWidth * i
				ctx.beginPath()
				ctx.moveTo(x, padTop)
				ctx.lineTo(x, padTop + plotH)
				ctx.stroke()
			}

			// K 线
			klines.forEach((k, i) => {
				const x = padLeft + barWidth * i + barWidth / 2
				const rising = k.close >= k.open
				const color = rising ? UP_COLOR : DOWN_COLOR
				ctx.strokeStyle = color
				ctx.fillStyle = color
				ctx.lineWidth = 1
				// 影线
				ctx.beginPath()
				ctx.moveTo(x, yOf(k.high))
				ctx.lineTo(x, yOf(k.low))
				ctx.stroke()
				// 实体
				const yOpen = yOf(k.open)
				const yClose = yOf(k.close)
				ctx.fillRect(x - candleWidth / 2, Math.min(yOpen, yClose), candleWidth, Math.max(1, Math.abs(yClose - yOpen)))
			})

			// 最新价虚线 + 端点光点 + 涨跌色价格标签牌
			if (this.latestPrice) {
				const y = yOf(this.latestPrice)
				const rising = this.upOrDownClass === this.upClass
				const color = rising ? UP_COLOR : DOWN_COLOR
				ctx.strokeStyle = color
				ctx.lineWidth = 1
				ctx.setLineDash([5, 4])
				ctx.beginPath()
				ctx.moveTo(padLeft, y)
				ctx.lineTo(padLeft + plotW, y)
				ctx.stroke()
				ctx.setLineDash([])
				ctx.save()
				ctx.shadowColor = color
				ctx.shadowBlur = 6
				ctx.fillStyle = color
				ctx.beginPath()
				ctx.arc(padLeft + plotW, y, 2.5, 0, Math.PI * 2)
				ctx.fill()
				ctx.restore()
				this.drawAxisChip(ctx, fmtPrice(this.latestPrice), y, color, '#fff', height, padLeft, plotW, 0)
			}

			// 开仓参考线 + 中性色标签牌
			if (this.openPriceRef != null) {
				const y = yOf(this.openPriceRef)
				ctx.strokeStyle = 'rgba(205, 216, 236, 0.45)'
				ctx.lineWidth = 1
				ctx.setLineDash([2, 4])
				ctx.beginPath()
				ctx.moveTo(padLeft, y)
				ctx.lineTo(padLeft + plotW, y)
				ctx.stroke()
				ctx.setLineDash([])
				// 空间不足时省略“开仓”前缀，避免标签牌溢出
				ctx.font = LABEL_FONT
				let label = '开仓 ' + fmtPrice(this.openPriceRef)
				if (ctx.measureText(label).width + 14 > padRight) {
					label = fmtPrice(this.openPriceRef)
				}
				this.drawAxisChip(ctx, label, y, 'rgba(205, 216, 236, 0.16)', 'rgba(232, 237, 247, 0.85)', height, padLeft, plotW, 4)
			}
		},
		/**
		 * 绘制右侧价格标签牌
		 *
		 * @param {object} ctx 画布上下文
		 * @param {string} label 标签文本
		 * @param {number} y 标签中心 y 坐标
		 * @param {string} background 背景色
		 * @param {string} color 文本色
		 * @param {number} height 画布高度
		 * @param {number} padLeft 左侧留白
		 * @param {number} plotW 绘图区宽度
		 * @param {number} offset 标签相对参考线的纵向偏移
		 */
		drawAxisChip(ctx, label, y, background, color, height, padLeft, plotW, offset) {
			ctx.font = LABEL_FONT
			const textWidth = ctx.measureText(label).width
			const chipW = textWidth + 12
			const chipH = 18
			let chipY = y - chipH / 2 + (offset || 0)
			if (chipY < 1) {
				chipY = 1
			}
			if (chipY + chipH > height - 1) {
				chipY = height - 1 - chipH
			}
			roundRectPath(ctx, padLeft + plotW + 4, chipY, chipW, chipH, 5)
			ctx.fillStyle = background
			ctx.fill()
			ctx.fillStyle = color
			ctx.textAlign = 'left'
			ctx.textBaseline = 'middle'
			ctx.fillText(label, padLeft + plotW + 10, chipY + chipH / 2 + 0.5)
		},
		/**
		 * 绘制空状态
		 *
		 * @param {object} ctx 画布上下文
		 * @param {number} width 宽度
		 * @param {number} height 高度
		 */
		drawEmpty(ctx, width, height) {
			ctx.fillStyle = 'rgba(205, 216, 236, 0.6)'
			ctx.font = '13px sans-serif'
			ctx.textAlign = 'center'
			ctx.fillText('等待行情...', width / 2, height / 2)
		}
	}
}
</script>

<style scoped>
.price-chart {
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	min-height: 0;
	background: linear-gradient(180deg, #0c1322, #0a0e18);
	border: 1px solid var(--fx-border);
	border-radius: 12px;
	overflow: hidden;
}

.price-chart__head {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 10px 14px;
	border-bottom: 1px solid var(--fx-border);
}

.price-chart__symbol {
	font-size: 13.5px;
	font-weight: 600;
	color: var(--fx-text);
	letter-spacing: 0.03em;
	padding: 3px 10px;
	border: 1px solid var(--fx-border);
	border-radius: 7px;
	background: rgba(255, 255, 255, 0.04);
	white-space: nowrap;
}

.price-chart__price {
	font-size: 24px;
	font-weight: 700;
	font-family: var(--fx-mono);
	font-variant-numeric: tabular-nums;
	transition: color 0.15s;
}

.price-chart__delta {
	font-size: 12.5px;
	font-family: var(--fx-mono);
	padding: 3px 9px;
	border-radius: 999px;
}

.price-chart__delta.is-up {
	background: var(--fx-up-soft);
	color: #ff8d84;
}

.price-chart__delta.is-down {
	background: var(--fx-down-soft);
	color: #43d9a0;
}

.price-chart__price.is-flash-up {
	animation: flash-up 0.6s ease-out;
}

.price-chart__price.is-flash-down {
	animation: flash-down 0.6s ease-out;
}

@keyframes flash-up {

	from {
		text-shadow: 0 0 20px rgba(245, 86, 74, 0.9);
	}

	to {
		text-shadow: 0 0 20px rgba(245, 86, 74, 0);
	}
}

@keyframes flash-down {

	from {
		text-shadow: 0 0 20px rgba(46, 189, 133, 0.9);
	}

	to {
		text-shadow: 0 0 20px rgba(46, 189, 133, 0);
	}
}

.price-chart__canvas {
	flex: 1;
	width: 100%;
	min-height: 0;
}

.is-up {
	color: var(--fx-up);
}

.is-down {
	color: var(--fx-down);
}

@media (prefers-reduced-motion: reduce) {

	.price-chart__price.is-flash-up,
	.price-chart__price.is-flash-down {
		animation: none;
	}
}
</style>
